import { api, setToken } from './api.ts';
import type { Session } from './api.ts';
import { agentCounts, agentsApi, pollWhile } from './agents-api.ts';
import type { AgentInfo, ProjectInfo } from './agents-api.ts';
import { icon } from './icons.ts';
import { h } from './ui.ts';
import { statusTone } from './views/agents/parts.ts';

/** The desktop app shell: a sidebar (talk, sections, projects with status dots) and the page (docs/FRONTEND.md). */

interface Snapshot {
  sessions: Session[];
  agents: AgentInfo[];
  projects: ProjectInfo[];
  approvals: unknown[];
}

const snapshot = async (): Promise<Snapshot> => {
  const [sessions, agents, projects, approvals] = await Promise.all([
    api.get<Session[]>('/sessions').catch(() => []),
    agentsApi.list().catch(() => []),
    agentsApi.projects().catch(() => []),
    api.get<unknown[]>('/approvals').catch(() => []),
  ]);
  return { sessions, agents, projects, approvals };
};

export function shell(active: string, view: HTMLElement): HTMLElement {
  const nav = h('nav', { class: 'sidebar', 'aria-label': 'Main' });
  let last = '';
  const draw = (s: Snapshot) => {
    // keep keyboard focus in the sidebar unless something it shows changed
    const sig = JSON.stringify([
      s.sessions.map((x) => !x.endedAt),
      s.agents.map((a) => [a.project, a.status]),
      s.projects.map((p) => p.slug),
      s.approvals.length,
    ]);
    if (sig === last) return;
    last = sig;
    nav.replaceChildren(...sidebar(active, s));
  };
  draw({ sessions: [], agents: [], projects: [], approvals: [] });
  const root = h('div', { class: 'shell' }, nav, h('main', { class: 'main', id: 'main' }, view));
  pollWhile(root, 5000, async () => draw(await snapshot()));
  void snapshot().then(draw);
  return root;
}

function sidebar(active: string, s: Snapshot): HTMLElement[] {
  const live = s.sessions.some((x) => !x.endedAt);
  const c = agentCounts(s.agents);
  const item = (href: string, ico: SVGElement, label: string, count?: HTMLElement | null) =>
    h(
      'a',
      {
        class: `nav-item ${active === href ? 'active' : ''}`,
        href,
        ...(active === href ? { 'aria-current': 'page' } : {}),
      },
      ico,
      h('span', { class: 'grow' }, label),
      count ?? null,
    );
  const counter = (n: number, tone = '') => (n ? h('span', { class: `nav-count ${tone}` }, String(n)) : null);
  return [
    brand(live),
    h(
      'a',
      { class: `talk-cta ${active === '#/talk' ? 'active' : ''}`, href: '#/talk' },
      h('span', { class: 'talk-cta-orb' }, icon.mic(14)),
      h('span', { class: 'grow' }, live ? 'Back to the talk' : 'Talk'),
      h('kbd', {}, 'voice'),
    ),
    h(
      'div',
      { class: 'nav-group' },
      item('#/chat', icon.send(), 'Chat'),
      item('#/agents', icon.bot(), 'Agents', c.asking ? counter(c.asking, 'warning') : counter(c.running, 'accent')),
      item('#/projects', icon.folder(), 'Projects'),
      item('#/knowledge', icon.book(), 'Knowledge'),
      item('#/activity', icon.pulse(), 'Activity'),
      item('#/approvals', icon.shield(), 'Approvals', counter(s.approvals.length, 'warning')),
    ),
    projectList(active, s),
    h(
      'div',
      { class: 'sidebar-foot' },
      h(
        'button',
        {
          class: 'btn ghost sm',
          type: 'button',
          onclick: () => {
            setToken('');
            location.reload();
          },
        },
        icon.out(14),
        'Sign out',
      ),
    ),
  ];
}

function brand(live: boolean): HTMLElement {
  return h(
    'div',
    { class: 'brand' },
    h('span', { class: 'brand-mark' }, icon.mark(14)),
    h('span', { class: 'grow' }, 'Assistant'),
    live
      ? h('span', { class: 'badge success', title: 'A session is live' }, h('span', { class: 'dot pulse' }), 'live')
      : null,
  );
}

function projectList(active: string, s: Snapshot): HTMLElement {
  return h(
    'div',
    { class: 'nav-group projects-nav' },
    h('div', { class: 'nav-label' }, 'Projects'),
    ...s.projects.map((p) => {
      const href = `#/projects/${encodeURIComponent(p.slug)}`;
      const mine = s.agents.filter((a) => a.project === p.slug);
      return h(
        'a',
        { class: `nav-item sub ${active === href ? 'active' : ''}`, href },
        h('span', { class: `dot tone-${statusTone(mine)}`, title: p.linearOrg ? 'built via Atlas' : '' }),
        h('span', { class: 'grow truncate' }, p.name),
        mine.length ? h('span', { class: 'nav-count' }, String(mine.length)) : null,
      );
    }),
    s.projects.length
      ? null
      : h('a', { class: 'nav-item sub muted', href: '#/projects' }, icon.plus(14), 'New project'),
  );
}
